import rawAxios from "axios";
import { SERVER_URL } from "../../../../../../../ServerLink";

const axios = rawAxios.create({ baseURL: SERVER_URL + "/" });

/**
 * API endpoints for lease and tenant flow
 */
const ENDPOINTS = {
  GET_LEASE_BY_ID: "v2/lease/tenant/get-lease-by-id/:id",
  GET_SIGNED_URL: "v2/lease/tenant/get-signed-url",
  UPLOAD_REQUESTED_DOCUMENTS: "v2/lease/tenant/upload-documents",
  PUBLISH_REQUESTED_TENANT_UPDATES: "v2/lease/tenant/publish-requested-signatures-documents",
  UPLOAD_FINAL_PDF: "v2/lease/tenant/:lease_id/upload-pdf",
};

/**
 * Get lease details by ID
 * @param {string} leaseId
 * @param {string} token - JWT token for authorization
 * @returns {Promise<Object>} Lease object
 */
export const getLeaseByIdForTenantSignature = async (leaseId, token) => {
  const url = ENDPOINTS.GET_LEASE_BY_ID.replace(':id', leaseId);
  const response = await axios.get(url, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  return response.data;
};

/**
 * Get signed URL for document
 * @param {Object} document
 * @param {string} token - JWT token for authorization
 * @returns {Promise<Object>}
 */
export const getSignedUrl = async (document, token) => {
  const params = { key: document.key };
  const { data: { data: response } } = await axios.get(ENDPOINTS.GET_SIGNED_URL, {
    params,
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  return response;
};

/**
 * Upload requested documents
 * @param {FormData} formData
 * @param {string} token - JWT token for authorization
 * @param {string} onUploadProgress - Callback functiom
 * @returns {Promise<Object>}
 */
export const uploadRequestedDocuments = async (formData, token, onUploadProgress) => {
  const response = await axios.post(ENDPOINTS.UPLOAD_REQUESTED_DOCUMENTS, formData, {
    headers: {
      'Authorization': `Bearer ${token}`,
      "Content-Type": "multipart/form-data"
    },
    onUploadProgress,
  });
  return response.data;
};

/**
 * Publish requested tenant updates (signatures and documents)
 * @param {Object} payload
 * @param {string} token - JWT token for authorization
 * @returns {Promise<Object>}
 */
export const publishRequestedTenantUpdates = async (values, token) => {
  const response = await axios.post(ENDPOINTS.PUBLISH_REQUESTED_TENANT_UPDATES, { values }, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  return response.data;
};

/**
 * Upload a lease PDF file
 * 
 * @param {string} leaseId - Lease ID to upload for
 * @param {File} file - The PDF file selected by the user
 * @param {string} token - JWT auth token
 * @returns {Promise<Object>} - Response from server
 */
export const uploadLeasePdf = async (leaseId, file, token) => {
  if (!leaseId || !file || !token) {
    throw new Error("Missing leaseId, file, or token");
  }
  const formData = new FormData();
  formData.append("lease_pdf", file);
  const url = ENDPOINTS.UPLOAD_FINAL_PDF.replace(':lease_id', leaseId);
  const response = await axios.post(url, formData, {
    headers: {
      "Authorization": `Bearer ${token}`,
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};


const GET_PROPERTY_URL = "/property/get-property";
const setMaxFilesForDocument = async (propertyId, documentName, token, setMaxFilesAllowed) => {
  try {
    if (!propertyId || !documentName || !token) {
      console.error("Missing required parameters:", { propertyId, documentName, token });
      setMaxFilesAllowed(1); // Default to 1 if parameters are missing
      return;
    }

    const response = await axios.get(`${GET_PROPERTY_URL}/${propertyId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.status === 200 && response.data.data) {
      const propertyData = response.data.data;
      const documentsToRequest = propertyData.documents_to_request || [];

      const matchedDocument = documentsToRequest.find(
        (doc) => doc.name.toLowerCase() === documentName.toLowerCase()
      );

      if (matchedDocument) {
        setMaxFilesAllowed(matchedDocument.no_of_files);
      } else {
        console.warn(`Document "${documentName}" not found in documents_to_request`);
        setMaxFilesAllowed(10); // Default to 1 if no match
      }
    } else {
      console.error("Invalid response from property API:", response);
      setMaxFilesAllowed(1); // Default to 1 on invalid response
    }
  } catch (error) {
    console.error("Error fetching property for max files:", error);
    setMaxFilesAllowed(1); // Default to 1 on error
  }
};

export { setMaxFilesForDocument };